import { OutboundCallState } from '@nimbus-global/features/shared/models/api/outbound-call-state.model';
import { TeamContactInfo } from '../../models/team-info';

export enum OutboundServiceSelectionBlockedReason {
  NoOutboundServices = 'NoOutboundServices',
}

export interface IOutboundServiceSelectionInput {
  inboundServiceId?: string;
  lastUsedOutboundServiceId?: string;
  services: TeamContactInfo[] | null | undefined;
  inboundServiceAllowsOutbound?: boolean;
}

export type OutboundServiceSelectionResult =
  | { kind: 'resolved'; serviceId: string; source: 'inbound' | 'lastUsed' | 'firstAvailable' }
  | { kind: 'blocked'; reason: OutboundServiceSelectionBlockedReason };

/**
 * Deterministic fallback chain for selecting the service used to start an outbound/callback call:
 * 1. Inbound service if it allows outbound and is enabled for the user.
 * 2. Last used outbound service stored in localStorage, if still enabled.
 * 3. First available outbound-enabled service.
 * 4. Blocked when the user has no outbound configuration at all.
 */
export function resolveOutboundService(input: IOutboundServiceSelectionInput): OutboundServiceSelectionResult {
  const enabledServices = (input.services ?? []).filter((s) => s.outboundCallState === OutboundCallState.Enabled);

  if (enabledServices.length === 0) {
    return { kind: 'blocked', reason: OutboundServiceSelectionBlockedReason.NoOutboundServices };
  }

  if (input.inboundServiceId && input.inboundServiceAllowsOutbound) {
    const inbound = enabledServices.find((s) => s.id === input.inboundServiceId || s.teamId === input.inboundServiceId);
    if (inbound) {
      return { kind: 'resolved', serviceId: inbound.id, source: 'inbound' };
    }
  }

  if (input.lastUsedOutboundServiceId) {
    const lastUsed = enabledServices.find((s) => s.teamId === input.lastUsedOutboundServiceId || s.id === input.lastUsedOutboundServiceId);
    if (lastUsed) {
      return { kind: 'resolved', serviceId: lastUsed.id, source: 'lastUsed' };
    }
  }

  return { kind: 'resolved', serviceId: enabledServices[0].id, source: 'firstAvailable' };
}
